import React, { useState, useContext, useEffect, useRef } from "react"
import _ from "lodash"

import StaffAssignedParticipants from "./StaffAssignedParticipants"
import StaffProfilePhoto from "../StaffProfilePhoto"
import { LoaderProgressBar } from "../Loaders"
import styles from "../../styles/StaffProfileBox.module.css"
import useStaffProfileBoxData from "../../hooks/useStaffProfileBoxData"
import { UserContext } from "../../context/userContext"
import { FirebaseContext } from "../../firebase"
import { parseDate } from "../../helpers/dateNormalization"
import {
  clearCopyFeedbackTimer,
  copyWithFeedback,
} from "../../helpers/copyFeedback"

export default function StaffProfileBox() {
  const userCtx = useContext(UserContext)
  const firebase = useContext(FirebaseContext)
  const staff_membership_id = userCtx.current_staff_membership_id
  const [copiedMembershipId, setCopiedMembershipId] = useState(false)
  const [copiedContactValue, setCopiedContactValue] = useState("")
  const copyResetTimerRef = useRef(null)
  const contactCopyResetTimerRef = useRef(null)

  const {
    staffData,
    currentStaffData,
    currentSelectedStaffUser,
    telerecoveryEnabled,
    profileBoxLoading,
    selectedStaffUserLoading,
    staffLoading,
  } = useStaffProfileBoxData(
    staff_membership_id,
    firebase,
    userCtx
  )

  // Normalize empty/null values to a consistent display placeholder.
  const displayValue = (value) => {
    if (_.isNil(value)) return "-"

    if (_.isString(value)) {
      const trimmedValue = value.trim()
      return trimmedValue.length ? trimmedValue : "-"
    }

    return value
  }

  useEffect(() => {
    return () => {
      clearCopyFeedbackTimer(copyResetTimerRef)
      clearCopyFeedbackTimer(contactCopyResetTimerRef)
    }
  }, [])

  if (_.isEmpty(staff_membership_id)) {
    return null
  }

  const isLoading = staffLoading || profileBoxLoading || selectedStaffUserLoading
  const hasStaffData =
    !_.isEmpty(currentStaffData) || !_.isEmpty(currentSelectedStaffUser)
  const isRefreshing = isLoading && hasStaffData

  if (isLoading && !hasStaffData) {
    return null
  }

  const fullName = _.compact([
    currentStaffData?.staff_name_first,
    currentStaffData?.staff_name_last,
  ]).join(" ")

  const staffMemberId = displayValue(
    currentSelectedStaffUser?.staff_membership_id ||
      staffData?.staffDataByStaffMembershipID?.staff_membership_id,
  )
  const staffRole = displayValue(
    _.startCase(currentStaffData?.staff_role_name?.replace(/_/g, "-")),
  )
  const supervisor = displayValue(currentStaffData?.staff_primary_supervisor_name)
  const hireDate = displayValue(currentStaffData?.staff_hire_date)
  const staffEmail = displayValue(
    currentStaffData?.staff_email_address ||
      currentSelectedStaffUser?.staff_email_address ||
      staffData?.staffDataByStaffMembershipID?.staff_email_address,
  )
  const staffPhone = displayValue(
    currentStaffData?.staff_phone_number ||
      currentStaffData?.staff_phone ||
      currentSelectedStaffUser?.staff_phone_number ||
      staffData?.staffDataByStaffMembershipID?.staff_phone_number,
  )
  const telerecoveryPhone = displayValue(
    currentStaffData?.staff_phone_telerecovery ||
      currentSelectedStaffUser?.staff_phone_telerecovery ||
      staffData?.staffDataByStaffMembershipID?.staff_phone_telerecovery,
  )
  const parsedLastSupervisionDate = parseDate(
    currentStaffData?.staff_last_supervision_date,
  )
  const lastSupervisionDate =
    parsedLastSupervisionDate instanceof Date &&
    !Number.isNaN(parsedLastSupervisionDate.getTime())
      ? parsedLastSupervisionDate.toLocaleDateString("en-US", {
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
        })
      : "-"
  const lastLoginDate = displayValue(currentStaffData?.staff_last_login)
  const selectedStaffId =
    currentSelectedStaffUser?.staff_id ||
    currentStaffData?.staff_id ||
    staffData?.staffDataByStaffMembershipID?.staff_id
  const viewedStaffRole =
    currentSelectedStaffUser?.staff_role ||
    staffData?.staffDataByStaffMembershipID?.rol_id

  const emailPills = staffEmail === "-" ? [] : staffEmail.split(", ").filter(Boolean)
  const phonePills = [staffPhone]
    .filter(Boolean)
    .filter((value) => value !== "-")
    .filter((value, index, values) => values.indexOf(value) === index)
  const telerecoveryPills = telerecoveryEnabled
    ? [telerecoveryPhone]
        .filter(Boolean)
        .filter((value) => value !== "-")
        .filter((value, index, values) => values.indexOf(value) === index)
    : []

  // Copy membership ID chip value and show temporary copied feedback.
  const copyMembershipId = async () => {
    await copyWithFeedback({
      value: staffMemberId,
      invalidValues: ["-"],
      setFeedbackState: setCopiedMembershipId,
      feedbackValue: true,
      resetValue: false,
      timerRef: copyResetTimerRef,
      errorMessage: "Failed to copy membership ID",
    })
  }

  // Copy contact pill value and highlight the copied pill briefly.
  const copyContactValue = async (value) => {
    await copyWithFeedback({
      value,
      invalidValues: ["-"],
      setFeedbackState: setCopiedContactValue,
      feedbackValue: value,
      resetValue: "",
      timerRef: contactCopyResetTimerRef,
      errorMessage: "Failed to copy contact value",
    })
  }

  const handleContactPillKeyDown = (event, value) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault()
      copyContactValue(value)
    }
  }

  return (
    <section className={styles.card}>
      {isRefreshing && (
        <div className={styles.loadingBarTop}>
          <LoaderProgressBar />
        </div>
      )}
      <div className={styles.layoutGrid}>
        <div className={styles.contentColumn}>
              <div className={styles.topRow}>
                <section
                  className={`${styles.panel} ${styles.panelAccent} ${styles.topCombinedPanel}`}
                >
                  <div className={`${styles.panelProfile} ${styles.topCombinedLeft}`}>
                    <div className={styles.photoWrap}>
                      <StaffProfilePhoto
                        staff_profile_image={currentStaffData?.staff_profile_image}
                      />
                    </div>

                    <div className={styles.identityBlock}>
                      <h2 className={styles.staffName}>{displayValue(fullName)}</h2>

                      <div className={styles.idRow}>
                        <button
                          type="button"
                          className={`${styles.idChipButton} ${copiedMembershipId ? styles.idChipCopied : ""}`}
                          onClick={copyMembershipId}
                          title={copiedMembershipId ? "Membership ID copied" : "Copy Membership ID"}
                          aria-label={copiedMembershipId ? "Membership ID copied" : "Copy Membership ID"}
                        >
                          <span className={styles.idChipLabel}>Membership ID:</span>
                          <span className={styles.idChipValue}>{staffMemberId}</span>
                        </button>
                      </div>
                    </div>
                  </div>

                  <div className={styles.topDivider} />

                  <div className={styles.topCombinedRight}>
                  <h3 className={`${styles.panelTitle} ${styles.sectionTitle}`}>
                    Supervision
                  </h3>

                  <div className={styles.rowsWrap}>
                    <div className={styles.infoLine}>
                      <span className={styles.lineLabel}>Supervisor:</span>
                      <span className={styles.lineValue}>{supervisor}</span>
                    </div>

                    <div className={styles.infoLine}>
                      <span className={styles.lineLabel}>Last Supervision:</span>
                      <span className={styles.lineValue}>{lastSupervisionDate}</span>
                    </div>
                  </div>
                  </div>
                </section>
              </div>

          <div className={styles.bottomRow}>
            <section className={styles.bottomSection}>
              <h3 className={`${styles.panelTitle} ${styles.sectionTitle}`}>
                Staff Details
              </h3>

              <div className={styles.rowsWrap}>
                <div className={styles.infoLine}>
                  <span className={styles.lineLabel}>Role:</span>
                  <span className={styles.lineValue}>{staffRole}</span>
                </div>

                <div className={styles.infoLine}>
                  <span className={styles.lineLabel}>Hire Date:</span>
                  <span className={styles.lineValue}>{hireDate}</span>
                </div>

                <div className={styles.infoLine}>
                  <span className={styles.lineLabel}>Last Login:</span>
                  <span className={styles.lineValue}>{lastLoginDate}</span>
                </div>
              </div>
            </section>

            <section className={`${styles.bottomSection} ${styles.bottomSectionRight}`}>
              <h3 className={`${styles.panelTitle} ${styles.sectionTitle}`}>
                Contact Details
              </h3>

              <div className={styles.rowsWrap}>
                <div className={styles.infoLine}>
                  <span className={styles.lineLabel}>Email:</span>
                  {emailPills.length > 0 ?
                    <div className={styles.contactPillRow}>
                      {emailPills.map((value) => (
                        (() => {
                          const helperText =
                            copiedContactValue === value ? "Email copied" : "Copy Email"

                          return (
                        <span
                          key={value}
                          className={`${styles.contactPill} ${styles.contactPillClickable} ${copiedContactValue === value ? styles.contactPillCopied : ""}`}
                          onClick={() => copyContactValue(value)}
                          onKeyDown={(event) => handleContactPillKeyDown(event, value)}
                          role="button"
                          tabIndex={0}
                          title={helperText}
                          aria-label={helperText}
                        >
                          {value}
                        </span>
                          )
                        })()
                      ))}
                    </div>
                  :
                    <span className={styles.lineValue}>-</span>
                  }
                </div>

                <div className={styles.infoLine}>
                  <span className={styles.lineLabel}>Phone:</span>
                  {phonePills.length > 0 ?
                    <div className={styles.contactPillRow}>
                      {phonePills.map((value) => (
                        (() => {
                          const helperText =
                            copiedContactValue === value ? "Phone copied" : "Copy Phone"

                          return (
                        <span
                          key={value}
                          className={`${styles.contactPill} ${styles.contactPillClickable} ${copiedContactValue === value ? styles.contactPillCopied : ""}`}
                          onClick={() => copyContactValue(value)}
                          onKeyDown={(event) => handleContactPillKeyDown(event, value)}
                          role="button"
                          tabIndex={0}
                          title={helperText}
                          aria-label={helperText}
                        >
                          {value}
                        </span>
                          )
                        })()
                      ))}
                    </div>
                  :
                    <span className={styles.lineValue}>-</span>
                  }
                </div>

                {telerecoveryEnabled && (
                  <div className={styles.infoLine}>
                    <span className={styles.lineLabel}>Telerecovery Phone:</span>
                    {telerecoveryPills.length > 0 ?
                      <div className={styles.contactPillRow}>
                        {telerecoveryPills.map((value) => (
                          (() => {
                            const helperText =
                              copiedContactValue === value
                                ? "Phone copied"
                                : "Copy Phone"

                            return (
                          <span
                            key={value}
                            className={`${styles.contactPill} ${styles.contactPillClickable} ${copiedContactValue === value ? styles.contactPillCopied : ""}`}
                            onClick={() => copyContactValue(value)}
                            onKeyDown={(event) => handleContactPillKeyDown(event, value)}
                            role="button"
                            tabIndex={0}
                            title={helperText}
                            aria-label={helperText}
                          >
                            {value}
                          </span>
                            )
                          })()
                        ))}
                      </div>
                    :
                      <span className={styles.lineValue}>-</span>
                    }
                  </div>
                )}
              </div>
            </section>
          </div>
        </div>

        <aside className={styles.assignedColumn}>
          <StaffAssignedParticipants
            assignedParticipants={currentStaffData?.assigned_participants}
            selectedStaffId={selectedStaffId}
            loggedInStaffId={userCtx.session.user.staff_id}
            viewedStaffRole={viewedStaffRole}
            containerClassName={styles.assignedContainer}
            className={styles.assignedBox}
            numberClassName={styles.assignedNumber}
            labelClassName={styles.assignedLabel}
          />
        </aside>
      </div>
    </section>
  )
}
